import React, { useState, useEffect, useCallback, useRef } from 'react';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExploreIcon from '@mui/icons-material/Explore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import AppIcon from '@mui/icons-material/Apps';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SchemaIcon from '@mui/icons-material/Schema';
import SearchIcon from '@mui/icons-material/Search';

import LinkIcon from '@mui/icons-material/Link';
import TimelineIcon from '@mui/icons-material/Timeline';
import StorageIcon from '@mui/icons-material/Storage';
import GavelIcon from '@mui/icons-material/Gavel';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import CodeIcon from '@mui/icons-material/Code';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import HttpIcon from '@mui/icons-material/Http';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Box, Typography, Paper, Button, List, ListItemButton, ListItemIcon, ToggleButton, ToggleButtonGroup, CircularProgress, Chip } from '@mui/material';


// Import the necessary tool components
import TokenDecoder from './TokenDecoder';
import GraphExplorer from './GraphExplorer';
import ManifestEditor from './ManifestEditor';
import ODataQueryBuilder from './ODataQueryBuilder';
import SamlTester from './SamlTester';
import AuthFlowTester from './AuthFlowTester';

import AppOverview from './AppOverview';
import { useMsal } from '@azure/msal-react';
import { Application } from "@microsoft/microsoft-graph-types";
import { Client } from "@microsoft/microsoft-graph-client";
import ClaimsMappingPolicyTool from './ClaimsMappingPolicyTool';
import SchemaExtensionsTool from './SchemaExtensionsTool';
// Removed unused InteractionRequiredAuthError import
import TokenService from '../utils/tokenService';

import TokenScopeRequester from './TokenScopeRequester';

import RawOAuthTester from './RawOAuthTester';
import OidcMetadataInspector from './OidcMetadataInspector';
import EntraEndpointsExplorer from './EntraEndpointsExplorer';
import MsalTraceViewer from './MsalTraceViewer';
import MsalCacheInspector from './MsalCacheInspector';
import ConsentManager from './ConsentManager';
import ConfidentialClientAuthDebugger from './ConfidentialClientAuthDebugger';
import TokenComparisonTool from './TokenComparisonTool';
import MsalErrorDecoder from './MsalErrorDecoder';

// ExtendedApplication interface removed (unused)

interface ToolsContentProps {
  searchQuery: string;
  onToolSelect?: (toolId: string) => void;
  onToolStateChange?: (isToolActive: boolean) => void;
}

type ToolComponentType = 
  | typeof TokenDecoder
  | typeof GraphExplorer
  | typeof ManifestEditor
  | typeof ODataQueryBuilder
  | typeof SamlTester
  | typeof AuthFlowTester
  | typeof SchemaExtensionsTool
  | typeof MsalTraceViewer
  | typeof MsalCacheInspector
  | typeof TokenComparisonTool
  | typeof MsalErrorDecoder;

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  component?: ToolComponentType;
  requiresToken?: boolean;
  isNew?: boolean;
  isPreview?: boolean;
  requiredPermissions?: string[];
}

const ToolsContent: React.FC<ToolsContentProps> = ({ searchQuery, onToolSelect, onToolStateChange }) => {
  const { instance, accounts } = useMsal();
  const [activeTool, setActiveTool] = useState<string | null>(null);
  // Removed unused confirmDialog and selectedTool state
  const [accessToken, setAccessToken] = useState<string>("");
  const [applications, setApplications] = useState<Application[]>([]);
  const [, setLoading] = useState<boolean>(false);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [serviceProviders, setServiceProviders] = useState<Record<string, string>>({});
  
  // State to track if we need to show app selection view
  const [showAppTesting, setShowAppTesting] = useState<boolean>(false);

  const [authInProgress, setAuthInProgress] = useState(false);
  const [authTool, setAuthTool] = useState<string | null>(null);

  // OPTIMIZED: Add state for applications loaded flag
  const [applicationsLoaded, setApplicationsLoaded] = useState<boolean>(false);

  // View mode toggle state with persistence
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = localStorage.getItem('toolsViewMode');
    return saved === 'list' ? 'list' : 'cards';
  });

  useEffect(() => {
    localStorage.setItem('toolsViewMode', viewMode);
  }, [viewMode]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: 'cards' | 'list' | null
  ) => {
    if (next) setViewMode(next);
  };

  // Keyboard shortcut will be attached after currentView is defined below

  // Create a TokenService instance
  const tokenService = React.useMemo(() => {
    return TokenService.getInstance(instance);
  }, [instance]);

    const restoringFromHistory = useRef(false);
  const previousPushedView = useRef<string | null>(null);

  // Helper to compute current logical view
  const currentView = (() => {
    if (activeTool === 'saml') return 'samlTester';
    if (activeTool === 'auth-flow') return 'authFlowTester';
    if (activeTool === 'schema-extensions') return 'schemaExtensions';
    if (activeTool === 'claimsMappingPolicy') return 'claimsMappingPolicy';
    if (activeTool === 'token-scope-requester') return 'tokenScopeRequester';
    if (activeTool === 'manifest-editor') return 'manifestEditor';
    if (activeTool === 'graph-explorer') return 'graphExplorer';
    if (activeTool === 'odata-builder') return 'odataBuilder';
    if (activeTool === 'entra-endpoints-explorer') return 'entraEndpointsExplorer';
    if (activeTool === 'jwt-decoder') return 'jwtDecoder';
    if (activeTool === 'consent-manager') return 'consentManager';
    if (activeTool === 'raw-oauth-tester') return 'rawOAuthTester';
    if (activeTool === 'confidential-client-auth') return 'confidentialClientAuth';
    if (activeTool === 'oidc-metadata-inspector') return 'oidcMetadataInspector';
    if (activeTool === 'msal-trace-viewer') return 'msalTraceViewer';
    if (activeTool === 'msal-cache-inspector') return 'msalCacheInspector';
    if (activeTool === 'token-comparison-tool') return 'tokenComparisonTool';
    if (activeTool === 'msal-error-decoder') return 'msalErrorDecoder';
    if (showAppTesting) return 'appTestingOverview';
    return 'toolsList';
  })();

  // Keyboard shortcut disabled intentionally for stability across tools.

  const pushViewState = (view: string, extra?: Record<string, any>) => {
    if (restoringFromHistory.current) return;
    if (previousPushedView.current === view) return;
    previousPushedView.current = view;
    const state: any = { appView: view, ...extra };
    window.history.pushState(state, '');
  };

  // Initialize base history entry once
  useEffect(() => {
    window.history.replaceState({ appView: 'toolsList' }, '');
  }, []);

  // React to browser Back / Forward
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      restoringFromHistory.current = true;
      const s = (e.state || {}) as { appView?: string; appId?: string };

      // Reset common state
      setActiveTool(null);
      setSelectedApp(null);
      setShowAppTesting(false);

      switch (s.appView) {
        case 'appTestingOverview':
          setShowAppTesting(true);
          break;

        // Views that depend on a selected app
        case 'samlTester': {
          if (s.appId) {
            const found = applications.find(a => a.id === s.appId || a.appId === s.appId) || null;
            if (found) setSelectedApp(found);
          }
          setActiveTool('saml');
          break;
        }

        case 'authFlowTester': {
          if (s.appId) {
            const found = applications.find(a => a.id === s.appId || a.appId === s.appId) || null;
            if (found) setSelectedApp(found);
          }
          setActiveTool('auth-flow');
          break;
        }

        // Views that do NOT require a selected app
        case 'schemaExtensions':
          setActiveTool('schema-extensions');
          break;

        case 'claimsMappingPolicy':
          setActiveTool('claimsMappingPolicy');
          break;

        case 'manifestEditor':
          setActiveTool('manifest-editor');
          break;

        case 'tokenScopeRequester':
          setActiveTool('token-scope-requester');
          break;

        case 'consentManager':
          setActiveTool('consent-manager');
          break;

        case 'graphExplorer':
          setActiveTool('graph-explorer');
          break;

        case 'odataBuilder':
          setActiveTool('odata-builder');
          break;

        case 'jwtDecoder':
          setActiveTool('jwt-decoder');
          break;

        case 'rawOAuthTester':
          setActiveTool('raw-oauth-tester');
          break;
        case 'oidcMetadataInspector':
          setActiveTool('oidc-metadata-inspector');
          break;
        case 'entraEndpointsExplorer':
          setActiveTool('entra-endpoints-explorer');
          break;
        case 'msalTraceViewer':
          setActiveTool('msal-trace-viewer');
          break;
        
        case 'msalCacheInspector':
          setActiveTool('msal-cache-inspector');
          break;
        
        case 'tokenComparisonTool':
          setActiveTool('token-comparison-tool');
          break;
        
        case 'msalErrorDecoder':
          setActiveTool('msal-error-decoder');
          break;

        case 'toolsList':
        default:
          if (onToolStateChange) onToolStateChange(false);
          break;
      }

      // Optional: if you want parent to know a tool is active after a popstate
      if (s.appView && s.appView !== 'toolsList') {
        if (onToolStateChange) onToolStateChange(true);
      }

      setTimeout(() => {
        restoringFromHistory.current = false;
        previousPushedView.current = s.appView || null;
      }, 0);
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [applications, onToolStateChange]);

  // Push history when logical view changes (forward navigation)
  useEffect(() => {
    if (currentView === 'toolsList') return; // Do not push base repeatedly
    const extra: Record<string, any> = {};
    if (selectedApp?.id || selectedApp?.appId) {
      extra.appId = selectedApp.id || selectedApp.appId;
    }
    pushViewState(currentView, extra);
  }, [currentView, selectedApp]);

  useEffect(() => {
    // This will run when the ToolsContent component is mounted/remounted
    const lastToolActivation = sessionStorage.getItem('toolActivationTime');
    const currentNavigation = new Date().toISOString();
    
    // If there's a significant time gap (user navigated away and came back)
    // or this is a fresh navigation, reset the tool state
    if (!lastToolActivation || 
        new Date(currentNavigation).getTime() - new Date(lastToolActivation).getTime() > 1000) {
      console.log('Navigation detected, resetting tools state');
      setActiveTool(null);
      setAccessToken("");
      setSelectedApp(null);
  // removed setSelectedTool (state no longer used)
      setShowAppTesting(false);
      sessionStorage.removeItem('lastActiveTool');
    }
    
    // Set cleanup function to update the last active tool when unmounting
    return () => {
      if (activeTool) {
        sessionStorage.setItem('lastActiveTool', activeTool);
      }
    };
  }, []);
  
  // OPTIMIZED: Load applications with proper caching and performance improvements
  useEffect(() => {
    // Only load if not already loaded and we have accounts
    if (!accounts.length || applicationsLoaded) return;
    
    const loadApplicationsOptimized = async () => {
      setLoading(true);
      try {
        const allApplications: Application[] = [];
        
        // Get token for Graph API
        const accessToken = await tokenService.getToken(
          ["Application.ReadWrite.All"], 
          accounts[0]
        );
        
        // Initialize Graph client
        const client = Client.init({ authProvider: (done) => done(null, accessToken) });
        
        // PRODUCTION: Dynamic pagination with proper error handling
        let nextLink: string | undefined;
        let pageCount = 0;
        const MAX_PAGES = 100; // Safety limit for any tenant
        let consecutiveEmptyPages = 0;
        const MAX_CONSECUTIVE_EMPTY = 3; // Stop after 3 consecutive empty pages
        
        console.log(`🔧 Tools: Starting application loading...`);
        
        do {
          pageCount++;
          console.log(`🔧 Tools: Fetching applications page ${pageCount}...`);
          
          let request = client.api("/applications")
            .select("id,appId,displayName,web,spa,api,appRoles,passwordCredentials,tags,identifierUris,appOwnerTenantId,signInAudience,publisherDomain,requiredResourceAccess")
            .top(100); // Maximum page size
          
          if (nextLink) {
            try {
              const url = new URL(nextLink);
              const skipToken = url.searchParams.get('$skiptoken');
              if (skipToken) {
                request = request.skipToken(skipToken);
              } else {
                console.warn(`🔧 Tools: NextLink without skipToken: ${nextLink}`);
                // Try using the full URL path
                const fullUrl = nextLink.replace('https://graph.microsoft.com/v1.0', '');
                request = client.api(fullUrl);
              }
            } catch (urlError) {
              console.error(`🔧 Tools: Error parsing nextLink: ${nextLink}`, urlError);
              break;
            }
          }
          
          const response = await request.get();
          
          // Check if we got results
          if (response.value && response.value.length > 0) {
            allApplications.push(...response.value);
            console.log(`🔧 Tools: Page ${pageCount}: Added ${response.value.length} applications (Total: ${allApplications.length})`);
            consecutiveEmptyPages = 0; // Reset counter
          } else {
            consecutiveEmptyPages++;
            console.warn(`🔧 Tools: Page ${pageCount}: No applications returned (${consecutiveEmptyPages}/${MAX_CONSECUTIVE_EMPTY})`);
            
            // If we get too many consecutive empty pages, something might be wrong
            if (consecutiveEmptyPages >= MAX_CONSECUTIVE_EMPTY) {
              console.warn(`🔧 Tools: Too many consecutive empty pages, stopping pagination`);
              break;
            }
          }
          
          // Update nextLink for next iteration
          nextLink = response['@odata.nextLink'];
          
          // Safety check to prevent infinite loops
          if (pageCount >= MAX_PAGES) {
            console.warn(`🔧 Tools: Reached maximum page limit (${MAX_PAGES}). Stopping pagination.`);
            break;
          }
          
        } while (nextLink);
        
        console.log(`✅ Tools: Application loading complete: ${allApplications.length} total applications loaded in ${pageCount} pages`);
        
        // Production logging without hardcoded expectations
        if (pageCount >= MAX_PAGES) {
          console.warn(`⚠️ Tools: Pagination stopped at safety limit. There may be more applications.`);
        }
        
        if (allApplications.length === 0) {
          console.info(`ℹ️ Tools: No applications found. This may be normal for new tenants.`);
        } else if (allApplications.length > 1000) {
          console.info(`ℹ️ Tools: Large number of applications (${allApplications.length}). Consider implementing lazy loading.`);
        }
        
        // Update state with all applications
        setApplications(allApplications);
        setApplicationsLoaded(true);
        
        // After loading applications, get service principals in background
        loadServicePrincipalsOptimized(allApplications);
      } catch (err) {
        console.error("🔧 Tools: Error loading applications:", err);
      } finally {
        setLoading(false);
      }
    };
    
    loadApplicationsOptimized();
  }, [instance, accounts, applicationsLoaded, tokenService]);

  // OPTIMIZED: Background service principal loading with better performance
  const loadServicePrincipalsOptimized = useCallback(async (apps: Application[]) => {
    if (!apps.length || !accounts.length) return;
    
    try {
      const accessToken = await tokenService.getToken(
        ["Application.ReadWrite.All"], 
        accounts[0]
      );
      
      const client = Client.init({ authProvider: (done) => done(null, accessToken) });
      const spMap: Record<string, string> = {};
      
      // OPTIMIZED: Process in smaller batches to prevent overwhelming the API
  const BATCH_SIZE = 20; // Reduced batch size for better reliability
      
      for (let i = 0; i < apps.length; i += BATCH_SIZE) {
        const batch = apps.slice(i, i + BATCH_SIZE);
        
        // Process batches with limited concurrency
        const batchPromises = batch
          .filter(app => app.appId) // Only process apps with appId
          .map(async (app) => {
            try {
              const spRes = await client
                .api(`/servicePrincipals?$filter=appId eq '${app.appId}'&$select=id,preferredSingleSignOnMode`)
                .get();
                
              if (spRes.value?.length) {
                const sp = spRes.value[0];
                spMap[app.appId!] = sp.preferredSingleSignOnMode || '';
              }
            } catch (err) {
              // Silently continue on errors to avoid breaking the entire process
              console.warn(`🔧 Tools: Failed to get SP for app ${app.displayName}:`, err);
            }
          });
        
        // Wait for current batch to complete
        await Promise.all(batchPromises);
        
        // Update state progressively
        setServiceProviders(prevMap => ({ ...prevMap, ...spMap }));
        
        // Small delay to prevent rate limiting
        if (i + BATCH_SIZE < apps.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`✅ Tools: Service principals loaded for ${Object.keys(spMap).length} applications`);
    } catch (err) {
      console.error("🔧 Tools: Failed to load service principals:", err);
    }
  }, [accounts, tokenService]);

  // Helper function to determine if an app is SAML-based
  const isSamlApp = (app: Application): boolean => {
    if (!app.appId) return false;
    return serviceProviders[app.appId] === 'saml';
  };

  // Define available tools (including SchemaExtensionsTool)
  const tools: Tool[] = [
    { 
      id: 'app-testing', 
      name: 'Auth Flow Tester', 
      description: 'Test applications with OAuth flows based on app type', 
      icon: <AppIcon sx={{ color: '#2196f3' }} />, // Blue
    },
    { 
      id: 'jwt-decoder', 
      name: 'JWT Token Decoder', 
      description: 'Decode and analyze JWT tokens', 
      icon: <VpnKeyIcon sx={{ color: '#4caf50' }} />, // Green
      component: TokenDecoder
    },
    {
      id: 'token-comparison-tool',
      name: 'Token Comparison Tool',
      description: 'Compare two JWT tokens side-by-side (ID vs Access, v1 vs v2)',
      icon: <CompareArrowsIcon sx={{ color: '#7c3aed' }} />, // Purple
      component: TokenComparisonTool,
      isNew: true
    },
    {
      id: 'msal-error-decoder',
      name: 'MSAL Error Decoder',
      description: 'Decode and troubleshoot MSAL authentication error codes',
      icon: <BugReportIcon sx={{ color: '#d32f2f' }} />, // Red
      component: MsalErrorDecoder,
      isNew: true
    },
    {
      id: 'msal-trace-viewer',
      name: 'MSAL Trace Viewer',
      description: 'Live view of msal-browser events grouped by correlationId',
      icon: <TimelineIcon sx={{ color: '#1e88e5' }} />,
      component: MsalTraceViewer,
      isNew: false,
    },
    {
      id: 'msal-cache-inspector',
      name: 'MSAL Cache Inspector',
      description: 'Inspect browser storage, understand token caching, and simulate acquireTokenSilent',
      icon: <StorageIcon sx={{ color: '#0288d1' }} />,
      component: MsalCacheInspector,
      isNew: true,
    },
    { 
      id: 'graph-explorer', 
      name: 'Graph Explorer', 
      description: 'Test Microsoft Graph API endpoints', 
      icon: <ExploreIcon sx={{ color: '#ff9800' }} />, // Orange
      component: GraphExplorer,
      // Request superset to avoid later escalations
      requiredPermissions: ["Application.ReadWrite.All"],
    },
    {
      id: 'odata-builder',
      name: 'OData Query Builder',
      description: 'Build $select, $filter, $orderby, $top, $count visually and run queries',
      icon: <CodeIcon sx={{ color: '#fb8c00' }} />, // Orange-ish
      component: ODataQueryBuilder,
      requiredPermissions: ["Application.ReadWrite.All"],
    },
    {
      id: 'entra-endpoints-explorer',
      name: 'Entra ID Endpoints Explorer',
      description: 'Comprehensive directory of Entra ID, Graph API, and Azure service endpoints',
      icon: <LinkIcon sx={{ color: '#5e35b1' }} />, // Deep purple
      component: EntraEndpointsExplorer,
    },
    {
      id: 'consent-manager',
      name: 'OAuth2 Consent Manager',
      description: 'View and delete OAuth2 permission grants for applications',
      icon: <GavelIcon sx={{ color: '#7c3aed' }} />, // Purple
      component: ConsentManager,
      requiredPermissions: ['Directory.Read.All']
    },
    { 
      id: 'manifest-editor', 
      name: 'Application Manifest Editor', 
      description: 'Edit application manifests directly', 
      icon: <BuildOutlinedIcon sx={{ color: '#9c27b0' }} />, // Purple
      component: ManifestEditor,
      // Show key permission for quick glance in card footer
      requiredPermissions: ["Application.ReadWrite.All"]
    },
    {
      id: 'token-scope-requester',
      name: 'Token & Consent Tester', // changed from "Token Scope Requester"
      description: 'Request delegated tokens for arbitrary scopes and inspect claims',
      icon: <LockOpenIcon sx={{ color: '#2e7d32' }} />, // Greenish
    },
    {
      id: 'raw-oauth-tester',
      name: 'Raw OAuth Tester', // changed from "Raw OAuth (no MSAL)"
      description: 'Test Implicit and Hybrid OAuth flows without MSAL; manually validate state, nonce, and tokens',
      icon: <HttpIcon sx={{ color: '#1565c0' }} />,
      isNew: false,
    },
    {
      id: 'confidential-client-auth',
      name: 'Confidential Client Auth Tester',
      description: 'Test confidential web app OAuth flows with client secrets (authorization code exchange)',
      icon: <SecurityIcon sx={{ color: '#c62828' }} />, // Deep red for confidential/sensitive
      isNew: false,
      isPreview: true,
    },
    {
      id: 'oidc-metadata-inspector',
      name: 'OIDC Metadata & JWKS Inspector',
      description: 'Fetch discovery document, JWKS, match & verify JWT signatures (RS256)',
      icon: <FormatListNumberedIcon sx={{ color: '#0d47a1' }} />,
      isNew: false,
    },
    {
      id: 'claimsMappingPolicy',
      name: 'Claims Mapping Policy Tool',
      description: 'Create, edit, and assign claims mapping policies to applications',
      icon: <ContentCopyIcon sx={{ color: '#00796b' }} />, // Teal
      requiredPermissions: [
        "Policy.Read.All",
        "Application.ReadWrite.All"
      ] // Directory.Read.All needed to list service principals already in Graph token
    },
    {
      id: 'schema-extensions',
      name: 'Claims Extensions Reference',
      description: 'View directory extensions and attributes for use in claims mapping policies',
      icon: <SchemaIcon sx={{ color: '#3f51b5' }} />, // Indigo
      // isNew: true,
      component: SchemaExtensionsTool, // Add this line to specify the component
      requiredPermissions: ["Directory.Read.All"]
    }
  ];

  // Define tool categories for visual organization
  const toolCategories = {
    // Keep auth-focused tools together
  "Authentication": ['app-testing', 'token-scope-requester', 'raw-oauth-tester', 'confidential-client-auth', 'jwt-decoder', 'token-comparison-tool', 'msal-trace-viewer', 'msal-cache-inspector'],
    // Technical troubleshooting and debugging helpers
    "Helpers & Debugging": ['msal-error-decoder', 'oidc-metadata-inspector', 'schema-extensions', 'entra-endpoints-explorer'],
    // General purpose Graph testing utilities (move up for visibility)
    "Graph & API Exploration": ['graph-explorer', 'odata-builder', 'consent-manager'],
    // Application-centric management actions
    "Application Management": ['manifest-editor', 'claimsMappingPolicy']
  } as const;

  // Filter tools based on search query
  const filteredTools = searchQuery
    ? tools.filter(tool => 
        tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tools;

  // Enhanced visual styling for categories with consistent dark indigo theme
  const getCategoryStyles = (category: string) => {
    // Keep a consistent, professional dark indigo header theme across categories
    const base = {
      color: '#312e81',
      borderColor: '#4c1d95',
      bgColor: '#e0e7ff'
    };
    const styles: Record<string, typeof base> = {
      'Authentication': base,
      'Helpers & Debugging': {
        color: '#115e59',
        borderColor: '#0f766e',
        bgColor: '#ccfbf1'
      },
      'Application Management': base,
      'Graph & API Exploration': base
    };
    return styles[category] || base;
  };

  // Enhanced tool card styling
  const getToolCardStyles = (toolId: string) => {
    const toolStyles = {
      'app-testing': { iconBg: '#eff6ff', iconColor: '#2563eb' },
      'jwt-decoder': { iconBg: '#f0fdf4', iconColor: '#059669' },
      'graph-explorer': { iconBg: '#fff7ed', iconColor: '#ea580c' },
      'odata-builder': { iconBg: '#fff7ed', iconColor: '#fb8c00' },
      'entra-endpoints-explorer': { iconBg: '#ede7f6', iconColor: '#5e35b1' },
      'consent-manager': { iconBg: '#f5f3ff', iconColor: '#7c3aed' },
      'manifest-editor': { iconBg: '#faf5ff', iconColor: '#9333ea' },
      'claimsMappingPolicy': { iconBg: '#f0fdfa', iconColor: '#0d9488' },
      'schema-extensions': { iconBg: '#eef2ff', iconColor: '#4f46e5' },
      'raw-oauth-tester': { iconBg: '#e3f2fd', iconColor: '#1565c0' },
      'confidential-client-auth': { iconBg: '#ffebee', iconColor: '#c62828' },
      'oidc-metadata-inspector': { iconBg: '#e3f2fd', iconColor: '#0d47a1' },
      'msal-trace-viewer': { iconBg: '#e3f2fd', iconColor: '#1e88e5' },
      'msal-cache-inspector': { iconBg: '#e1f5fe', iconColor: '#0288d1' },
      'msal-error-decoder': { iconBg: '#ffebee', iconColor: '#d32f2f' },
      'token-comparison-tool': { iconBg: '#f3e5f5', iconColor: '#7c3aed' },
    };
    return toolStyles[toolId as keyof typeof toolStyles] || { iconBg: '#f8fafc', iconColor: '#64748b' };
  };

  // (Removed) compact permission pills helper — no longer used in list view

  // Enhanced tool selection handler with comprehensive token management
  const handleToolSelect = async (toolId: string) => {
    console.log(`🔧 Tools: Tool selected: ${toolId}`);

    // Notify parent that a tool is now active
    if (onToolStateChange) {
      onToolStateChange(true);
    }
    
    // Allow tools that don't require MSAL sign-in or use existing applications data
    if (
      toolId === 'raw-oauth-tester' ||
      toolId === 'oidc-metadata-inspector' ||
      toolId === 'entra-endpoints-explorer' ||
      toolId === 'msal-trace-viewer' ||
      toolId === 'msal-cache-inspector' ||
      toolId === 'jwt-decoder' ||
      toolId === 'token-comparison-tool' ||
      toolId === 'msal-error-decoder' ||
      toolId === 'app-testing' ||
      toolId === 'consent-manager' ||
      toolId === 'confidential-client-auth'
    ) {
      sessionStorage.setItem('toolActivationTime', new Date().toISOString());
      // Special-case: app-testing opens the AppOverview (not a direct tool component)
      if (toolId === 'app-testing') {
        setShowAppTesting(true);
        setActiveTool(null);
      } else {
        setActiveTool(toolId);
      }
      setAuthInProgress(false);
      setAuthTool(null);
      return;
    }

    if (!accounts || accounts.length === 0) {
      alert("Please sign in first to access tools.");
      return;
    }

    // Check if token service is available
    if (!tokenService) {
      console.error("🔧 Tools: Token service not available");
      alert("Authentication service is not available. Please refresh the page.");
      return;
    }

  // Show authentication in progress for this tool
  setAuthInProgress(true);
  setAuthTool(toolId);

    const currentAccount = accounts[0];
    const selectedToolInfo = tools.find(tool => tool.id === toolId);
    
    // Determine required scopes based on tool
  let requiredScopes: string[] = []; // Default to none; only request when explicitly needed
    
    try {
      // Clear any previous tool state
      sessionStorage.removeItem('lastActiveTool');
      
      switch(toolId) {
        case 'schema-extensions':
          requiredScopes = ["Directory.Read.All"];
          break;
        case 'claimsMappingPolicy':
          requiredScopes = [
            "Policy.Read.All",
            // "Policy.ReadWrite.ApplicationConfiguration", // moved to component level for write actions
            "Application.ReadWrite.All"
          ]; // Directory.Read.All to allow servicePrincipal reads already in Graph token 
          break;
        case 'graph-explorer':
          requiredScopes = ["Application.ReadWrite.All"];
          break;
        case 'manifest-editor':
          requiredScopes = ["Application.ReadWrite.All"];
          break;
        case 'token-scope-requester':
          // Activate immediately; component will handle scope prompts
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          setActiveTool(toolId);
          setAuthInProgress(false);
          setAuthTool(null);
          return;
        default:
          // Use tool-specific scopes if defined; otherwise, none
          requiredScopes = selectedToolInfo?.requiredPermissions || [];
      }
      
      console.log(`🔧 Tools: Acquiring token for ${toolId} with scopes:`, requiredScopes);
      
      // For GraphExplorer specifically, we use an enhanced UX approach
      if (toolId === 'graph-explorer') {
        try {
          // UPDATED: Try to use the existing token first without requesting additional scopes
          console.log("🔧 Tools: Attempting to use existing token for GraphExplorer...");
          
          // Try to get existing token silently with minimal scopes
          const accessToken = await tokenService.getToken(
            requiredScopes, 
            currentAccount
          );
          
          // If successful, proceed without popup
          setAccessToken(accessToken);
          console.log(`🔧 Tools: Using existing token for ${toolId}`);
          
          // Record activation time for navigation detection
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          
          // Directly activate the selected tool (defer to next tick so accessToken state is applied)
          setTimeout(() => setActiveTool(toolId), 0);
        } catch (error: any) {
          console.log("🔧 Tools: Existing token insufficient, will request permissions as needed:", error);
          
          // If superset token is not available, try to get it silently via MSAL
          try {
            const response = await instance.acquireTokenSilent({
              scopes: ["Application.ReadWrite.All"],
              account: currentAccount
            });
            setAccessToken(response.accessToken);
            console.log("🔧 Tools: Using Application.ReadWrite.All token for GraphExplorer");
            
            // Record activation time for navigation detection
            sessionStorage.setItem('toolActivationTime', new Date().toISOString());
            
            // Directly activate the selected tool (defer to next tick so accessToken state is applied)
            setTimeout(() => setActiveTool(toolId), 0);
          } catch (silentError: any) {
            // Only if we can't get even a basic token, then request interactive auth
            console.log("🔧 Tools: No existing token available, requesting interactive auth");
            
            const response = await instance.acquireTokenPopup({
              scopes: ["Application.ReadWrite.All"],
              account: currentAccount
            });
            
            setAccessToken(response.accessToken);
            console.log("🔧 Tools: Interactive authentication successful");
            
            // Record activation time for navigation detection
            sessionStorage.setItem('toolActivationTime', new Date().toISOString());
            
            // Directly activate the selected tool (defer to next tick so accessToken state is applied)
            setTimeout(() => setActiveTool(toolId), 0);
          }
        }
      } else {
        // If no scopes are required, activate tool without acquiring a token
        if (!requiredScopes || requiredScopes.length === 0) {
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          if (toolId === 'app-testing') {
            setShowAppTesting(true);
            setActiveTool(null);
          } else {
            // Directly activate the selected tool (defer to next tick so accessToken state is applied)
            setTimeout(() => setActiveTool(toolId), 0);
          }
          return;
        }
        // Standard flow for other tools using TokenService
        try {
          // Use the token service to get token with required scopes
          const accessToken = await tokenService.getToken(
            requiredScopes, 
            currentAccount
          );
          
          // Store the acquired token
          setAccessToken(accessToken);
          console.log(`🔧 Tools: Token successfully acquired for ${toolId}`);
          
          // Record activation time for navigation detection
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          
          // Tool-specific handling
          if (toolId === 'app-testing') {
            setShowAppTesting(true);
            setActiveTool(null);
          } else {
            // Defer activation so accessToken state is applied before first render
            setTimeout(() => setActiveTool(toolId), 0);
          }
        } catch (error: any) {
          // Re-throw to be caught by outer catch
          throw error;
        }
      }
    } catch (error: any) {
      console.error(`🔧 Tools: Error acquiring token for ${toolId}:`, error);
      
      // Enhanced error categorization and handling
      if (error.name === "InteractionRequiredAuthError" || 
          error.name === "BrowserAuthError" ||
          error.errorCode === "consent_required") {
        
        console.log("🔧 Tools: Interactive authentication required, attempting popup flow");
        
        try {
          // Try interactive authentication via popup
          const popupScopes = selectedToolInfo?.requiredPermissions || requiredScopes;
          console.log("🔧 Tools: Requesting consent via popup for scopes:", popupScopes);
          
          const response = await instance.acquireTokenPopup({
            scopes: popupScopes,
            account: currentAccount
          });
          
          setAccessToken(response.accessToken);
          console.log("🔧 Tools: Interactive authentication successful");
          
          // Record activation time for navigation detection
          sessionStorage.setItem('toolActivationTime', new Date().toISOString());
          
          // Tool-specific handling after interactive auth
          if (toolId === 'app-testing') {
            setShowAppTesting(true);
            setActiveTool(null);
          } else {
            // Directly activate the tool (defer to next tick so accessToken state is applied)
            setTimeout(() => setActiveTool(toolId), 0);
          }
        } catch (popupError: any) {
          console.error("🔧 Tools: Interactive authentication failed:", popupError);
          
          // Detailed popup error handling
          if (popupError.name === "BrowserAuthError" && 
              popupError.message.includes("popup")) {
            alert("Popup blocked. Please enable popups for this site and try again.");
          } else if (popupError.name === "UserCancelledError" || 
                    popupError.message.includes("cancelled")) {
            console.log("🔧 Tools: User cancelled the authentication process");
            // No alert needed as this was user's choice
          } else {
            alert(`Unable to acquire necessary permissions for ${selectedToolInfo?.name || toolId}. Please try again.`);
          }
        }
      } else if (error.name === "ClientAuthError" || 
                error.errorCode === "client_error") {
        alert(`Authentication configuration error. Please try again later or contact support.`);
      } else {
        // Generic error handling
        alert(`Failed to access ${selectedToolInfo?.name || toolId}. ${error.message || "Please try again."}`);
      }
    } finally {
      // Always clear the authentication progress state
      setAuthInProgress(false);
      setAuthTool(null);
    }
  };
  
  // Handle app selection for testing - dynamically determine SAML or OAuth flow
  const handleAppSelected = (app: Application) => {
    setSelectedApp(app);
    setShowAppTesting(false);
    
    // Determine which tester to use based on app type
    if (isSamlApp(app)) {
      setActiveTool('saml');
    } else {
      setActiveTool('auth-flow');
    }
  };

  // Handle saving manifest
  const handleSaveManifest = async (updatedManifest: any): Promise<any> => {
    if (!accounts.length) {
      throw new Error("No active user account found");
    }
    
    try {
      setLoading(true);
      
      // Use TokenService instead of direct MSAL calls
      const accessToken = await tokenService.getToken(
        ["Application.ReadWrite.All"], 
        accounts[0]
      );
      
      // Initialize Graph client
      const client = Client.init({
        authProvider: (done) => done(null, accessToken)
      });
      
      // Make the PATCH request to update the application
      const result = await client
        .api(`/applications/${updatedManifest.id}`)
        .patch(updatedManifest);
      
      // Refresh applications list to reflect changes
      const updatedApps = applications.map(app => 
        app.id === updatedManifest.id ? {...app, ...updatedManifest} : app
      );
      setApplications(updatedApps);
      
      return result;
    } catch (error) {
      console.error("🔧 Tools: Error saving application manifest:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // *** UPDATED handleBack function ***
  const handleBack = () => {
    // Use browser history so we pop to the prior state instead of creating a new one
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    // Fallback (very first entry or no history): manually reset
    setActiveTool(null);
    setSelectedApp(null);
    setShowAppTesting(false);
    if (onToolStateChange) onToolStateChange(false);
  };

  // Function for handling token decoding in tools
  const handleDecodedToken = (token: any) => {
    // console.log("Decoded token:", token); // disabled for security
    // Implement any callback handling needed
  };

  if (showAppTesting) {
    return (
      <AppOverview
        apps={applications}
        onBack={() => {
          if (window.history.length > 1) {
            window.history.back();
            return;
          }
          if (onToolStateChange) onToolStateChange(false);
          setShowAppTesting(false);
          setActiveTool(null);
          setSelectedApp(null);
        }}
        onSelectAppForTesting={handleAppSelected}
      />
    );
  }

  // Type-safe rendering of active tool components
  const renderActiveTool = () => {
    if (!activeTool) return null;

    // In test runs, provide a non-empty fallback token to avoid race conditions in async state updates
    const tokenForTools = accessToken || (process.env.NODE_ENV === 'test' ? 'test-token' : '');
    
    // Special handling for SAML and Auth Flow tools which aren't in the tools array
    if (activeTool === 'saml' && selectedApp) {
      return <SamlTester 
        app={selectedApp as any}
        onBack={handleBack} 
        msalInstance={instance}
      />;
    }
    
    if (activeTool === 'auth-flow' && selectedApp) {
      return <AuthFlowTester 
        app={selectedApp as any}
        onBack={handleBack} 
        onDecodedTokenTested={handleDecodedToken} 
      />;
    }
    
    // Add condition to handle the Schema Extensions Tool
    if (activeTool === 'schema-extensions') {
      return <SchemaExtensionsTool onBack={handleBack} />;
    }

    // Token Scope Requester
    if (activeTool === 'token-scope-requester') {
      return (
        <TokenScopeRequester
          onBack={handleBack}
          tokenService={tokenService}
          account={accounts[0]}
          msalInstance={instance}
        />
      );
    }

    // Handle the Claims Mapping Policy Tool
    if (activeTool === 'claimsMappingPolicy') {
      return <ClaimsMappingPolicyTool 
        onBack={handleBack}
        accessToken={tokenForTools}
      />;
    }
    
    // Raw OAuth Tester (no MSAL) - handle explicitly
    if (activeTool === 'raw-oauth-tester') {
      return <RawOAuthTester onBack={handleBack} />;
    }
    // Confidential Client Auth Debugger - standalone component
    if (activeTool === 'confidential-client-auth') {
      return <ConfidentialClientAuthDebugger onBack={handleBack} applications={applications} />;
    }
    if (activeTool === 'oidc-metadata-inspector') {
      return <OidcMetadataInspector onBack={handleBack} />;
    }
    if (activeTool === 'entra-endpoints-explorer') {
      return <EntraEndpointsExplorer onBack={handleBack} />;
    }
    if (activeTool === 'msal-trace-viewer') {
      return <MsalTraceViewer onBack={handleBack} />;
    }
    if (activeTool === 'msal-cache-inspector') {
      return <MsalCacheInspector onBack={handleBack} />;
    }
    
    // Consent Manager - standalone component
    if (activeTool === 'consent-manager') {
      return <ConsentManager onBack={handleBack} />;
    }
    
    // For other tools, find them in the tools array
    const tool = tools.find(t => t.id === activeTool);
    if (!tool?.component) {
      return (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6">
            {tool?.name || "Tool"} is not implemented yet
          </Typography>
          <Button 
            variant="outlined"
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Tools
          </Button>
        </Box>
      );
    }

    // We'll use type casting and conditional rendering instead of a switch statement
    if (activeTool === 'graph-explorer') {
      const GraphExplorerComponent = tool.component as typeof GraphExplorer;
      return <GraphExplorerComponent 
        accessToken={tokenForTools} 
        onBack={handleBack} 
      />;
    }
    
    if (activeTool === 'manifest-editor') {
      const ManifestEditorComponent = tool.component as typeof ManifestEditor;
      return <ManifestEditorComponent 
        applications={applications}
        onBack={handleBack}
        onSave={handleSaveManifest}
      />;
    }
    
    if (activeTool === 'odata-builder') {
      const ODataComponent = tool.component as typeof ODataQueryBuilder;
  return <ODataComponent accessToken={tokenForTools} onBack={handleBack} />;
    }
    
    if (activeTool === 'jwt-decoder') {
      const TokenDecoderComponent = tool.component as typeof TokenDecoder;
      return <TokenDecoderComponent 
        onBack={handleBack} 
      />;
    }
    
    if (activeTool === 'token-comparison-tool') {
      const TokenComparisonComponent = tool.component as typeof TokenComparisonTool;
      return <TokenComparisonComponent onBack={handleBack} />;
    }
    
    if (activeTool === 'msal-error-decoder') {
      const MsalErrorDecoderComponent = tool.component as typeof MsalErrorDecoder;
      return <MsalErrorDecoderComponent onBack={handleBack} />;
    }
    
    // Default fallback
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">
          Tool configuration error
        </Typography>
        <Button 
          variant="outlined"
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Tools
        </Button>
      </Box>
    );
  };

  // If a tool is active, render it
  if (activeTool) {
    return (
      <>
        {renderActiveTool()}
        
        {/* Authentication in progress overlay - moved here so it works globally */}
        {authInProgress && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <Paper sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h6">
                Authenticating for {authTool ? tools.find(t => t.id === authTool)?.name || authTool : 'tool'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                A popup window may appear briefly for consent verification
              </Typography>
            </Paper>
          </Box>
        )}
      </>
    );
  }

  // Enhanced tools list with better visual design
return (
  <Box sx={{ pb: 2 }}>
    {/* View mode toggle */}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={viewMode}
        onChange={handleViewModeChange}
        aria-label="tools view mode"
      >
        <ToggleButton value="cards" aria-label="cards view">
          <ViewModuleIcon fontSize="small" />
          <Typography variant="caption" sx={{ ml: 0.5 }}>Cards</Typography>
        </ToggleButton>
        <ToggleButton value="list" aria-label="list view">
          <ViewListIcon fontSize="small" />
          <Typography variant="caption" sx={{ ml: 0.5 }}>List</Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
    {Object.entries(toolCategories).map(([category, toolIds], index) => {
  const categoryTools = filteredTools.filter(tool => (toolIds as readonly string[]).includes(tool.id));
      
      if (categoryTools.length === 0) return null;
      
      const categoryStyle = getCategoryStyles(category);
      const isLastCategory = index === Object.entries(toolCategories).length - 1;
      
      return (
        <div key={category}>
          <Box sx={{ mb: 5 }}>
            {/* Your existing Enhanced Category Header */}
            <Box
              sx={{
                mb: 2.5,
                px: 0,
                py: 1,
                borderRadius: 1,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '1.2rem',
                  color: categoryStyle.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {category}
                
                {/* Subtle divider line instead of prominent box */}
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: categoryStyle.borderColor,
                    opacity: 0.3,
                    ml: 2
                  }}
                />
                
                {/* More subtle tool count */}
                {/* <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    backgroundColor: categoryStyle.bgColor,
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    color: categoryStyle.color,
                    border: `1px solid ${categoryStyle.borderColor}20`,
                    opacity: 0.8
                  }}
                >
                  {categoryTools.length} tool{categoryTools.length !== 1 ? 's' : ''}
                </Box> */}
              </Typography>
            </Box>

            {/* Tools rendering based on view mode */}
            {viewMode === 'cards' ? (
              <Box
                sx={{
                  display: 'grid',
                  gap: 2.5,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  '@media (max-width: 768px)': {
                    gridTemplateColumns: '1fr'
                  }
                }}
              >
                {categoryTools.map(tool => {
                  const cardStyle = getToolCardStyles(tool.id);
                  
                  return (
                    <Paper
                      key={tool.id}
                      elevation={0}
                      sx={{ 
                        borderRadius: 3,
                        border: '1.5px solid #e2e8f0',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                          borderColor: '#cbd5e1',
                          '& .tool-icon': {
                            transform: 'scale(1.1) rotate(5deg)',
                          },
                          '& .tool-card-bg': {
                            opacity: 1,
                            transform: 'scale(1.05)',
                          }
                        }
                      }}
                      onClick={() => handleToolSelect(tool.id)}
                    >
                      {/* Background Pattern */}
                      <Box
                        className="tool-card-bg"
                        sx={{
                          position: 'absolute',
                          top: -20,
                          right: -20,
                          width: 100,
                          height: 100,
                          background: `linear-gradient(135deg, ${cardStyle.iconColor}15, ${cardStyle.iconColor}05)`,
                          borderRadius: '50%',
                          opacity: 0.7,
                          transform: 'scale(0.8)',
                          transition: 'all 0.3s ease',
                          zIndex: 0,
                        }}
                      />

                      <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                        {/* Tool Header */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2.5,
                              backgroundColor: cardStyle.iconBg,
                              border: `2px solid ${cardStyle.iconColor}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              transition: 'all 0.3s ease',
                              width: 56,
                              height: 56,
                            }}
                            className="tool-icon"
                          >
                            {React.cloneElement(tool.icon as React.ReactElement, {
                              sx: {
                                fontSize: '1.5rem',
                                color: cardStyle.iconColor,
                                filter: `drop-shadow(0 2px 4px ${cardStyle.iconColor}30)`,
                              }
                            })}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  color: '#1e293b',
                                  lineHeight: 1.2,
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {tool.name}
                              </Typography>
                              {tool.isPreview && (
                                <Chip 
                                  label="Preview" 
                                  size="small" 
                                  color="info"
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem', 
                                    fontWeight: 700,
                                    '& .MuiChip-label': { px: 1 }
                                  }} 
                                />
                              )}
                              {tool.isNew && (
                                <Chip 
                                  label="NEW" 
                                  size="small" 
                                  color="success"
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem', 
                                    fontWeight: 700,
                                    '& .MuiChip-label': { px: 1 }
                                  }} 
                                />
                              )}
                            </Box>

                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#64748b',
                                lineHeight: 1.5,
                                fontSize: '0.9rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {tool.description}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Tool Footer */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: 2,
                            borderTop: '1px solid #f1f5f9',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: cardStyle.iconColor,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {category.toLowerCase()}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: '#94a3b8',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              overflow: 'hidden'
                            }}
                          >
                            {/* Compact permission pills (do not increase card height) */}
                            {Array.isArray(tool.requiredPermissions) && tool.requiredPermissions.length > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
                                {tool.requiredPermissions.slice(0, 2).map((perm) => (
                                  <Box
                                    key={perm}
                                    component="span"
                                    title={perm}
                                    sx={{
                                      px: 0.75,
                                      py: 0.1,
                                      borderRadius: '999px',
                                      border: '1px solid #e2e8f0',
                                      backgroundColor: '#f8fafc',
                                      color: '#64748b',
                                      fontSize: '0.65rem',
                                      fontWeight: 600,
                                      whiteSpace: 'nowrap',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {perm.replace('.ReadWrite.', '.RW.').replace('.Read.', '.R.').replace('Application.', 'App.').replace('Policy.', 'Pol.').replace('Directory.', 'Dir.')}
                                  </Box>
                                ))}
                                {tool.requiredPermissions.length > 2 && (
                                  <Box
                                    component="span"
                                    sx={{
                                      px: 0.75,
                                      py: 0.1,
                                      borderRadius: '999px',
                                      border: '1px solid #e2e8f0',
                                      backgroundColor: '#f8fafc',
                                      color: '#64748b',
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      whiteSpace: 'nowrap',
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    +{tool.requiredPermissions.length - 2}
                                  </Box>
                                )}
                              </Box>
                            )}

                          </Box>
                        </Box>
                      </Box>

                      {/* Hover Effect Border */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: 3,
                          border: `2px solid ${cardStyle.iconColor}`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          pointerEvents: 'none',
                          '.MuiPaper-root:hover &': {
                            opacity: 0.3,
                          }
                        }}
                      />
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <List disablePadding sx={{ 
                border: '1px solid #e2e8f0', 
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: '#ffffff'
              }}>
                {categoryTools.map((tool, index) => {
                  const cardStyle = getToolCardStyles(tool.id);
                  const isLast = index === categoryTools.length - 1;
                  
                  return (
                    <ListItemButton
                      key={tool.id}
                      onClick={() => handleToolSelect(tool.id)}
                      sx={{ 
                        py: 2,
                        px: 2.5,
                        borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
                        borderLeft: `4px solid transparent`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: '#f8fafc',
                          borderLeftColor: cardStyle.iconColor,
                          transform: 'translateX(4px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          '& .tool-list-icon': {
                            transform: 'scale(1.1)',
                            backgroundColor: cardStyle.iconColor,
                            '& svg': {
                              color: '#ffffff !important',
                            }
                          }
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <Box
                          className="tool-list-icon"
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            backgroundColor: cardStyle.iconBg,
                            border: `1.5px solid ${cardStyle.iconColor}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2.5,
                            flexShrink: 0,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {React.cloneElement(tool.icon as React.ReactElement, {
                            sx: {
                              fontSize: '1.3rem',
                              color: cardStyle.iconColor,
                              transition: 'color 0.2s ease'
                            }
                          })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                fontWeight: 600, 
                                lineHeight: 1.3,
                                fontSize: '1rem',
                                color: '#1e293b'
                              }}
                            >
                              {tool.name}
                            </Typography>
                            {tool.isPreview && (
                              <Chip 
                                label="Preview" 
                                size="small" 
                                color="info"
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.6rem', 
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 0.75 }
                                }} 
                              />
                            )}
                            {tool.isNew && (
                              <Chip 
                                label="NEW" 
                                size="small" 
                                color="success"
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.6rem', 
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 0.75 }
                                }} 
                              />
                            )}
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#64748b',
                              lineHeight: 1.5,
                              fontSize: '0.875rem'
                            }}
                          >
                            {tool.description}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          {!isLastCategory && (
            <Box
              sx={{
                my: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {[1, 2, 3, 4, 5].map(dot => (
                <Box
                  key={dot}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#94a3b8',
                    opacity: dot === 3 ? 1 : 0.6, // Center dot more prominent
                  }}
                />
              ))}
            </Box>
          )}
        </div>
      );
    })}
      
      {/* Enhanced No Results State */}
      {filteredTools.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <SearchIcon sx={{ fontSize: '2rem', color: '#94a3b8' }} />
          </Box>
          <Typography variant="h6" sx={{ mb: 1, color: '#64748b', fontWeight: 600 }}>
            No tools found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No tools match your search for "<strong>{searchQuery}</strong>"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search terms or browse all available tools.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ToolsContent;